import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useMemo } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { StagePicker } from '@/features/projects/components/StagePicker'
import type { ProjectRowPatch } from '@/features/projects/row-edit'
import { AssigneeCell, DueDateCell } from '@/features/tasks/components'
import {
  CompanyMonogram,
  CompanySpine,
  DueDateBadge,
  StatusPill,
  UserAvatar,
} from '@/shared/components/common'
import { DragHandle, useDndSensors } from '@/shared/components/dnd'
import { createAnnouncements, createInstructions } from '@/shared/components/dnd/announcements'
import { useI18n, useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'
import { anchorForDrop, type Anchor } from '@/shared/lib/rank'
import type { ProjectSummary, Stage } from '@/shared/types/domain'

/**
 * Editar desde la fila: fase, responsable y fechas.
 *
 * Es lo que cambia a diario en un proyecto, y abrir la ficha para cada cosa
 * hacía que la tabla quedara atrasada. Sin esto, la tabla es de sólo lectura.
 */
export interface PortfolioRowEditing {
  /** Las fases del juego de columnas DE ESE proyecto (cada empresa tiene el suyo). */
  stagesFor: (project: ProjectSummary) => readonly Stage[]
  onPatch: (project: ProjectSummary, patch: ProjectRowPatch) => void
}

export interface PortfolioTableProps {
  projects: readonly ProjectSummary[]
  /**
   * Reordenar arrastrando. La tabla puede estar filtrada, así que no se mandan
   * los vecinos visibles sino un ancla: ver `anchorForDrop`.
   * Sin esto, la tabla no se arrastra.
   */
  onReorder?: (projectId: string, anchor: Anchor) => void
  /** Se intentó un movimiento que no tiene sentido (un permanente). */
  onRejectedDrop?: () => void
  editing?: PortfolioRowEditing
  className?: string
}

/**
 * El semáforo: mismo catálogo y mismos tonos que la ficha del proyecto, para
 * que "Con problemas" se vea igual en las dos pantallas.
 */
const HEALTH: Record<string, { key: string; tone: 'done' | 'overdue' | 'feedback' }> = {
  green: { key: 'common.health.green', tone: 'done' },
  yellow: { key: 'common.health.yellow', tone: 'feedback' },
  red: { key: 'common.health.red', tone: 'overdue' },
}

const HEAD = 'px-2 py-1.5 text-2xs font-normal tracking-wide text-text-3 uppercase'

/**
 * Tabla densa: el default del dia a dia.
 *
 * El orden de las columnas es el del plan y no es casual: el numero de
 * prioridad y la empresa primero (de quien es esto y que tan arriba esta), y
 * los contadores de abiertas/vencidas al final, que es donde el ojo termina el
 * barrido.
 */
export function PortfolioTable({
  projects,
  onReorder,
  onRejectedDrop,
  editing,
  className,
}: PortfolioTableProps) {
  const t = useT()
  const sensors = useDndSensors()
  const ids = useMemo(() => projects.map((project) => project.id), [projects])
  const announcements = useMemo(() => createAnnouncements(t, 'list'), [t])
  const instructions = useMemo(() => createInstructions(t), [t])

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || !onReorder || active.id === over.id) return
    const fromIndex = projects.findIndex((project) => project.id === active.id)
    const toIndex = projects.findIndex((project) => project.id === over.id)
    const moved = projects[fromIndex]
    if (!moved || toIndex === -1) return
    // Permanentes y priorizados son dos listas de prioridad distintas.
    const anchor = anchorForDrop(
      projects,
      fromIndex,
      toIndex,
      (project) => project.is_ongoing === moved.is_ongoing,
    )
    if (!anchor || projects[toIndex]?.is_ongoing !== moved.is_ongoing) {
      onRejectedDrop?.()
      return
    }
    onReorder(moved.id, anchor)
  }

  return (
    <div
      className={cn(
        'hairline overflow-x-auto rounded-[var(--radius-card)] bg-surface-1',
        className,
      )}
    >
      <table className="w-full min-w-[1040px]">
        <thead>
          <tr className="hairline-b">
            <th scope="col" className={cn(HEAD, onReorder ? 'w-14 text-right' : 'w-10 text-right')}>
              #
            </th>
            <th scope="col" className={cn(HEAD, 'text-left')}>
              {t('common.label.company')}
            </th>
            <th scope="col" className={cn(HEAD, 'text-left')}>
              {t('common.label.project')}
            </th>
            <th scope="col" className={cn(HEAD, 'text-left')}>
              {t('common.label.phase')}
            </th>
            <th scope="col" className={cn(HEAD, 'text-left')}>
              {t('common.label.owner')}
            </th>
            <th scope="col" className={cn(HEAD, 'text-left')}>
              {t('portfolio.table.start')}
            </th>
            <th scope="col" className={cn(HEAD, 'text-left')}>
              {t('portfolio.table.target')}
            </th>
            <th scope="col" className={cn(HEAD, 'text-left')}>
              {t('portfolio.table.nextDue')}
            </th>
            <th scope="col" className={cn(HEAD, 'text-right')}>
              {t('portfolio.table.openTasks')}
            </th>
            <th scope="col" className={cn(HEAD, 'text-right')}>
              {t('portfolio.table.overdueTasks')}
            </th>
            {/*
              El semáforo, ÚLTIMO.

              La API devuelve `health` por proyecto y el portafolio no tenía la
              columna: el proyecto en rojo se veía igual que los demás (QA
              funcional 1.7). Nació pegada a la fase —las dos dicen cómo va—
              pero ahí partía en dos el bloque que se lee de corrido: qué es el
              proyecto, de quién es y cuándo vence. Al final cierra la fila sin
              interrumpir nada.
            */}
            <th scope="col" className={cn(HEAD, 'text-left')}>
              {t('portfolio.table.health')}
            </th>
          </tr>
        </thead>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          accessibility={{ announcements, screenReaderInstructions: instructions }}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <tbody>
              {projects.map((project) => (
                <SortableRow
                  key={project.id}
                  id={project.id}
                  label={project.name}
                  // Sin `onReorder` la tabla es de sólo lectura; los permanentes no
                  // compiten por prioridad y no se arrastran.
                  disabled={!onReorder || project.is_ongoing}
                  disabledReason={
                    project.is_ongoing ? t('priorities.ongoing.rejectReason') : undefined
                  }
                  showHandle={Boolean(onReorder)}
                >
                  {(handle) => (
                    <>
                      <td className="num px-2 text-right text-xs text-text-3">
                        <span className="flex items-center justify-end gap-1">
                          {handle}
                          {/* Los proyectos ongoing no llevan numero de prioridad. */}
                          {project.is_ongoing ? (
                            <span aria-label={t('portfolio.table.noPriority')}>—</span>
                          ) : (
                            project.position
                          )}
                        </span>
                      </td>

                      <td className="px-2">
                        <Link
                          to={`/companies/${project.company.id}`}
                          className="flex items-center gap-2 hover:text-signal"
                        >
                          <CompanySpine
                            hue={project.company.color_hue}
                            label={project.company.name}
                            className="h-4"
                          />
                          <CompanyMonogram
                            name={project.company.name}
                            hue={project.company.color_hue}
                          />
                          <span className="truncate text-xs text-text-2">
                            {project.company.name}
                          </span>
                        </Link>
                      </td>

                      <td className="px-2">
                        <Link
                          to={`/projects/${project.id}`}
                          className="flex items-center gap-2 truncate text-xs text-text-1 hover:text-signal"
                        >
                          <span className="truncate">{project.name}</span>
                          {project.is_ongoing ? (
                            <StatusPill tone="ongoing" variant="quiet">
                              {t('portfolio.ongoing')}
                            </StatusPill>
                          ) : null}
                        </Link>
                      </td>

                      <td className="px-2">
                        <StageCell project={project} editing={editing} />
                      </td>

                      <td className="px-2">
                        {editing ? (
                          <AssigneeCell
                            user={project.internal_owner}
                            projectId={project.id}
                            withName
                            label={t('portfolio.edit.owner', { proyecto: project.name })}
                            onChange={(userId, user) =>
                              editing.onPatch(project, {
                                internal_owner_id: userId,
                                internal_owner: user,
                              })
                            }
                            className="-ml-1"
                          />
                        ) : (
                          <UserAvatar user={project.internal_owner} withName />
                        )}
                      </td>

                      <td className="px-2">
                        {editing ? (
                          <DueDateCell
                            plain
                            align="start"
                            value={project.start_date}
                            label={t('portfolio.edit.start', { proyecto: project.name })}
                            onChange={(fecha) => editing.onPatch(project, { start_date: fecha })}
                            className="-ml-1"
                          />
                        ) : (
                          <PlainDate value={project.start_date} />
                        )}
                      </td>

                      <td className="px-2">
                        {/* La entrega sí vence: lleva semáforo, salvo con el proyecto culminado. */}
                        {editing ? (
                          <DueDateCell
                            align="start"
                            value={project.target_end_date}
                            isDone={project.stage.category === 'done'}
                            label={t('portfolio.edit.target', { proyecto: project.name })}
                            onChange={(fecha) =>
                              editing.onPatch(project, { target_end_date: fecha })
                            }
                            className="-ml-1"
                          />
                        ) : (
                          <DueDateBadge
                            date={project.target_end_date}
                            isDone={project.stage.category === 'done'}
                          />
                        )}
                      </td>

                      <td className="px-2">
                        <DueDateBadge date={project.next_due_date} />
                      </td>

                      <td className="num px-2 text-right text-xs text-text-2">
                        {project.open_tasks}
                      </td>

                      <td className="num px-2 text-right text-xs">
                        {project.overdue_tasks > 0 ? (
                          <span className="font-medium text-overdue">
                            <span aria-hidden="true">! </span>
                            {project.overdue_tasks}
                          </span>
                        ) : (
                          <span className="text-text-3">0</span>
                        )}
                      </td>

                      <td className="px-2">
                        {project.health ? (
                          <StatusPill tone={HEALTH[project.health]!.tone} variant="quiet">
                            {t(HEALTH[project.health]!.key)}
                          </StatusPill>
                        ) : (
                          <span className="text-2xs text-text-3">{t('common.health.unknown')}</span>
                        )}
                      </td>
                    </>
                  )}
                </SortableRow>
              ))}
            </tbody>
          </SortableContext>
        </DndContext>
      </table>
    </div>
  )
}

/** La fase: el pill de un clic de la ficha, con las fases del set del proyecto. */
function StageCell({
  project,
  editing,
}: {
  project: ProjectSummary
  editing?: PortfolioRowEditing
}) {
  const t = useT()
  const stages = editing?.stagesFor(project) ?? []

  // Sin las fases de su set no hay a dónde moverlo: mejor mostrarlo quieto que
  // ofrecer columnas de otra empresa que el backend rechaza con un 409.
  if (!editing || stages.length === 0) {
    return <StatusPill category={project.stage.category}>{project.stage.name}</StatusPill>
  }

  return (
    <StagePicker
      size="sm"
      stage={project.stage}
      stages={stages}
      listLabel={t('portfolio.edit.stage', { proyecto: project.name })}
      onChange={(stage) => editing.onPatch(project, { stage })}
      className="w-fit"
    />
  )
}

function PlainDate({ value }: { value: string | null }) {
  const { t, formatDate } = useI18n()
  return (
    <span className={cn('num text-xs', value ? 'text-text-2' : 'text-text-3')}>
      {value ? formatDate(value) : t('common.state.none')}
    </span>
  )
}

interface SortableRowProps {
  id: string
  label: string
  disabled: boolean
  disabledReason?: string
  showHandle: boolean
  children: (handle: ReactNode) => ReactNode
}

/**
 * Una fila arrastrable. Es la `<tr>` misma la que se mueve —no un `div` adentro
 * de la tabla—, así las columnas no se desarman durante el arrastre. Se agarra
 * sólo desde el handle: el resto de la fila son links.
 */
function SortableRow({
  id,
  label,
  disabled,
  disabledReason,
  showHandle,
  children,
}: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled })

  const style: CSSProperties = {
    height: 'var(--row-h)',
    transform: CSS.Translate.toString(transform),
    transition,
  }

  return (
    <tr
      ref={setNodeRef}
      style={style}
      data-dragging={isDragging || undefined}
      className={cn(
        'hairline-b transition-colors duration-[var(--motion-fast)] hover:bg-surface-2/60',
        isDragging && 'relative z-10 bg-surface-2 opacity-80',
      )}
    >
      {children(
        showHandle ? (
          <DragHandle
            ref={setActivatorNodeRef}
            label={label}
            disabled={disabled}
            disabledReason={disabledReason}
            isDragging={isDragging}
            {...attributes}
            {...listeners}
          />
        ) : null,
      )}
    </tr>
  )
}
